import React, { memo, useEffect, useState, useCallback } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { getQuickBooksLog, triggerQuickBooksSync, extractQbError } from '../actions';
import type { QuickBooksLogEntry, QuickBooksLogProps } from '../types';

import classes from './quickBooksLog.module.css';

const getStatusBadgeClass = (status: QuickBooksLogEntry['status']): string => {
  switch (status) {
    case 'success':
      return 'bg-success';
    case 'pending':
      return 'bg-warning text-dark';
    case 'failed':
      return 'bg-danger';
    case 'duplicate':
      return 'bg-secondary';
    default:
      return 'bg-light text-dark';
  }
};

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

function QuickBooksLog({ locationId, ssoToken }: QuickBooksLogProps) {
  const [logs, setLogs] = useState<QuickBooksLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getQuickBooksLog(locationId, ssoToken, 100);
      setLogs(data.logs || []);
    } catch (err) {
      setError(extractQbError(err, 'Failed to load activity log'));
      // eslint-disable-next-line no-console
      console.error('Error fetching QuickBooks log:', err);
    } finally {
      setLoading(false);
    }
  }, [locationId, ssoToken]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // The backend exposes only a bulk entity sync (no per-row retry), so a failed
  // row is re-attempted by triggering a full sync and refreshing the log.
  const handleSync = useCallback(async () => {
    try {
      setSyncing(true);
      setError(null);
      await triggerQuickBooksSync(locationId, ssoToken, 'all');
      await fetchLogs();
    } catch (err) {
      setError(extractQbError(err, 'Failed to start QuickBooks sync'));
      // eslint-disable-next-line no-console
      console.error('Error triggering sync:', err);
    } finally {
      setSyncing(false);
    }
  }, [locationId, ssoToken, fetchLogs]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading activity log...</p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">QuickBooks Activity Log</h3>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Syncing...
              </>
            ) : (
              'Sync Now'
            )}
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={fetchLogs}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button type="button" className="btn btn-link btn-sm" onClick={fetchLogs}>
            Retry
          </button>
        </div>
      )}

      {logs.length === 0 ? (
        <div className="alert alert-info" role="alert">
          No activity logs found. Logs will appear here once contacts and opportunities are synced with QuickBooks.
        </div>
      ) : (
        <div className={classes.tableContainer}>
          <table className="table table-striped table-hover">
            <thead className="table-light">
              <tr>
                <th>Type</th>
                <th>Operation</th>
                <th>Status</th>
                <th>Reference</th>
                <th>QuickBooks ID</th>
                <th>Error</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`${log.type}-${log.contactId || log.opportunityId || index}-${index}`}>
                  <td className="text-capitalize">{log.type}</td>
                  <td>{log.operation || 'N/A'}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(log.status)}`}>
                      {log.status || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <code className={classes.idCode}>{log.contactId || log.opportunityId || ''}</code>
                  </td>
                  <td>
                    <code className={classes.idCode}>{log.qbCustomerId || log.qbInvoiceId || ''}</code>
                  </td>
                  <td>
                    {log.error && (
                      <span className="text-danger" title={log.error}>
                        {log.error.length > 40 ? `${log.error.substring(0, 40)}...` : log.error}
                      </span>
                    )}
                  </td>
                  <td>{formatDate(log.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const QuickBooksLogMemo = memo(QuickBooksLog, areEqual);

export { QuickBooksLogMemo as QuickBooksLog };
